<template>
  <div class="crud-toolbar">
    <button
      v-if="showBack"
      class="crud-toolbar-back"
      type="button"
      aria-label="Volver"
      title="Volver"
      @click="$emit('back')"
    >
      <i class="bi bi-arrow-left" aria-hidden="true"></i>
    </button>

    <h3 class="crud-toolbar-title">{{ title }}</h3>

    <div class="crud-toolbar-center">
      <div class="crud-toolbar-search">
        <i class="bi bi-search" aria-hidden="true"></i>
        <input
          :value="modelValue"
          @input="$emit('update:modelValue', $event.target.value)"
          type="search"
          class="crud-toolbar-search-input"
          :placeholder="searchPlaceholder"
          :aria-label="searchPlaceholder"
        />
        <button
          v-if="modelValue"
          class="crud-toolbar-search-clear"
          type="button"
          @click="$emit('update:modelValue', '')"
          aria-label="Limpiar búsqueda"
        >
          <i class="bi bi-x" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <div class="crud-toolbar-extra">
      <slot name="extra" />
    </div>

    <div class="crud-toolbar-actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup>
defineProps({
  title: { type: String, required: true },
  modelValue: { type: String, default: "" },
  searchPlaceholder: { type: String, default: "Buscar..." },
  showBack: { type: Boolean, default: false },
});
defineEmits(["update:modelValue", "back"]);
</script>

<style scoped>
.crud-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--d-space-2) var(--d-space-4);
  min-height: 56px;
  height: auto;
  box-sizing: border-box;
  background: var(--d-cream);
  border-bottom: 1px solid var(--d-border);
  margin: -2rem -2rem var(--d-space-5) -2rem;
  padding: var(--d-space-2) 2rem;
}

@media (max-width: 991.98px) {
  .crud-toolbar {
    height: auto;
    min-height: 52px;
    /* Cancela también el colchón entre el navbar móvil (65px) y el
       padding-top de .main-content (85px) para quedar pegada arriba. */
    margin: -20px -1rem var(--d-space-4) -1rem;
    padding: var(--d-space-2) 1rem;
  }
}

.crud-toolbar-back {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--d-brown);
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.crud-toolbar-back:hover {
  background: var(--d-nav-hover-bg);
}

.crud-toolbar-title {
  margin: 0;
  color: var(--d-brown);
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

@media (max-width: 991.98px) {
  /* El título de la sección ya se muestra centrado en el navbar móvil
     (SidebarGeneral.vue) — aquí sobra y solo resta espacio. */
  .crud-toolbar-title {
    display: none;
  }
}

.crud-toolbar-center {
  flex: 1;
  display: flex;
  justify-content: center;
  min-width: 0;
}

.crud-toolbar-search {
  position: relative;
  width: 100%;
  max-width: 420px;
}

.crud-toolbar-search i.bi-search {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-text-soft);
  font-size: 0.72rem;
  pointer-events: none;
}

.crud-toolbar-search-input {
  width: 100%;
  height: 32px;
  border: 1.5px solid var(--d-border-soft);
  border-radius: var(--d-radius-pill);
  background: var(--d-field-soft);
  color: var(--d-brown);
  padding: 0 32px;
  font-size: 0.78rem;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

/* type="search" trae su propio botón nativo de limpiar en Chrome/Edge que
   se dibujaba encima del nuestro (dos "x"). Lo ocultamos y dejamos solo el
   botón personalizado de abajo. */
.crud-toolbar-search-input::-webkit-search-cancel-button,
.crud-toolbar-search-input::-webkit-search-decoration {
  -webkit-appearance: none;
  appearance: none;
}

.crud-toolbar-search-input::placeholder {
  color: var(--d-text-soft);
  font-size: 0.75rem;
}

.crud-toolbar-search-input:focus {
  border-color: var(--d-green);
  background: var(--d-field);
  box-shadow: 0 0 0 3px var(--d-nav-hover-bg);
}

.crud-toolbar-search-clear {
  position: absolute;
  right: 3px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--d-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background 0.2s ease;
}

.crud-toolbar-search-clear:hover {
  background: var(--d-nav-hover-bg);
}

.crud-toolbar-extra {
  display: flex;
  align-items: center;
  gap: var(--d-space-2);
  flex-shrink: 0;
}

.crud-toolbar-extra:empty {
  display: none;
}

.crud-toolbar-actions {
  display: flex;
  align-items: center;
  gap: var(--d-space-2);
  flex-shrink: 0;
}

@media (max-width: 991.98px) {
  .crud-toolbar-extra:not(:empty) {
    order: 1;
    flex: 1 1 100%;
  }
  .crud-toolbar-center {
    order: 2;
  }
  .crud-toolbar-actions {
    order: 3;
  }
}
</style>
